/**
 * Sending a command to one television.
 *
 * There was no client for this at all until now: `reload`, `restart_app` and
 * `clear_cache` existed on the device and in the API and could only be issued by hand
 * with curl. The one that made a button unavoidable is `apply_update`, because it is the
 * step a person in the shop has to be talked through on the telephone.
 *
 * Commands are delivered on the device's next heartbeat, so "immediately" means within
 * about a minute rather than instantly. A command nobody collects expires after a day.
 */

import type { CommandType } from '@pbtv/shared';
import { requestJson } from './api.js';

/**
 * When the device should be handed the command.
 *
 * `now` is the next heartbeat. `window` holds it until the store's update window comes
 * round, which is what stops an update dialog covering the menu board during service —
 * and a store with no window set never opens one, so a windowed command there waits
 * until somebody sets one or re-sends it as `now`.
 */
export type CommandDelivery = 'now' | 'window';

export async function sendCommand(
  screenId: string,
  type: CommandType,
  deliver: CommandDelivery = 'now',
): Promise<void> {
  await requestJson(`/admin/screens/${encodeURIComponent(screenId)}/commands`, {
    method: 'POST',
    body: { type, payload: {}, deliver },
  });
}
